import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    Sparkles, ArrowRight, ShieldCheck, Plane, Landmark, Building, Trees,
    Hotel, Scale, Crown, Star, Compass, Send, Loader2, ChevronRight,
    Network, Calculator,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";

// Approx FJD→USD for tier-thresholding only. Not used in any displayed price.
const FJD_TO_USD = 0.46;

// ---------------------- Service catalog (subset relevant to detail pages) ----------------------
const SERVICE_DEFS = {
    investor_visa: {
        slug: "investor-visa",
        label: "Investor permit & visa",
        blurb: "We lodge the permit, escort the medicals, and clear it before settlement.",
        icon: Plane,
    },
    rbf_inflow: {
        slug: "rbf-capital-inflow",
        label: "RBF capital inflow approval",
        blurb: "Without this, foreign-source funds can't legally land in your Fiji bank.",
        icon: Landmark,
    },
    entity_formation: {
        slug: "fiji-entity-formation",
        label: "Fiji entity formation",
        blurb: "Companies Office + FRCS TIN + FNPF registration in one workflow.",
        icon: Building,
    },
    bank_intro: {
        slug: "bank-introduction",
        label: "Bank introduction & KYC",
        blurb: "Pre-vetted KYC pack into our preferred Fiji banks — typical open in 2 weeks.",
        icon: Landmark,
    },
    firca: {
        slug: "firca-approval",
        label: "FIRCA foreign investment approval",
        blurb: "Foreign Investment Registration Certificate prepared, lodged, and chased.",
        icon: ShieldCheck,
    },
    tltb: {
        slug: "tltb-native-land",
        label: "iTLTB native land lease",
        blurb: "Mataqali consent coordination, lease structuring, and goodwill negotiation.",
        icon: Trees,
    },
    tourism_hotel_aid: {
        slug: "tourism-hotel-aid",
        label: "Tourism Hotel Aid Act incentives",
        blurb: "Up to 25% investment allowance and 10-year tax holiday for qualifying tourism builds.",
        icon: Hotel,
    },
    eia: {
        slug: "environment-impact-assessment",
        label: "Environment Impact Assessment (EIA)",
        blurb: "Required for almost every coastal or large-footprint project. We coordinate end-to-end.",
        icon: ShieldCheck,
    },
    council_planning: {
        slug: "council-planning-permit",
        label: "Council planning & building permits",
        blurb: "Direct relationships at Suva, Nadi, Lautoka, and Sigatoka councils.",
        icon: Building,
    },
    tax_structuring: {
        slug: "tax-structuring",
        label: "Tax structuring & FRCS optimisation",
        blurb: "VAT, PAYE, and corporate-tax structure designed before you transact.",
        icon: Scale,
    },
    business_due_diligence: {
        slug: "business-due-diligence",
        label: "Business due-diligence audit",
        blurb: "Independent financial + legal review before you sign any acquisition.",
        icon: ShieldCheck,
    },
    investor_match: {
        slug: "investor-match",
        label: "Capital partner match",
        blurb: "Curated introductions to vetted equity partners aligned with your thesis.",
        icon: Network,
    },
    // Phase E — property-kind-specific services
    strata_body_corp: {
        slug: "strata-body-corp",
        label: "Strata title & body-corp setup",
        blurb: "Apartment + condo titling, body-corporate constitution, levy structure, and AGM coordination.",
        icon: Building,
    },
    home_inspection: {
        slug: "home-inspection",
        label: "Pre-purchase home inspection",
        blurb: "Independent structural + termite + electrical + plumbing report before settlement.",
        icon: ShieldCheck,
    },
    council_change_of_use: {
        slug: "council-change-of-use",
        label: "Council change-of-use consent",
        blurb: "Required when repurposing residential to commercial, mixed-use to retail, etc. Council relationships included.",
        icon: Building,
    },
    commercial_valuation: {
        slug: "commercial-valuation",
        label: "Commercial valuation & yield model",
        blurb: "Independent rental yield, cap-rate, and DCF valuation tied to leasable square metres.",
        icon: Calculator,
    },
};

// ---------------------- Tier definitions for upsell card ----------------------
const TIERS = {
    explorer: {
        id: "explorer",
        label: "Explorer",
        price: "$499 USD",
        icon: Compass,
        accent: "fiji-green",
        oneLiner: "Decide if Fiji's right — without flying in.",
    },
    investor: {
        id: "investor",
        label: "Investor",
        price: "$4,999 USD",
        icon: Star,
        accent: "coral",
        oneLiner: "Capital deployed in 90 days, end-to-end.",
    },
    developer: {
        id: "developer",
        label: "Developer",
        price: "$24,999 USD",
        icon: Crown,
        accent: "ocean",
        oneLiner: "Project orchestration with ministry-level liaison.",
    },
};

// ---------------------- Context → service ranking ----------------------
function pickServices(context) {
    const { kind, ownershipType, zoning, projectType, priceFjd, capitalRequiredFjd, propertyKind } = context;
    const dollars = Number(priceFjd || capitalRequiredFjd || 0) * FJD_TO_USD;
    const isHighValue = dollars >= 1_000_000;
    const isFreehold = (ownershipType || "").toLowerCase().includes("freehold");
    const isItaukeiOrTltb = /itaukei|tltb|native|crown/i.test(ownershipType || "");
    const isTourism = /tourism|hotel|resort|hospitality/i.test(`${zoning || ""} ${projectType || ""}`);
    const isBusiness = kind === "business";

    // Phase E — property-kind awareness
    const pk = (propertyKind || "land").toLowerCase();
    const isApartment = pk === "apartment";
    const isResidentialHome = pk === "residential_home";
    const isCommercialBuilding = pk === "commercial_building";
    const isMixedUseBuilding = pk === "mixed_use_building";
    const isBuilding = isApartment || isResidentialHome || isCommercialBuilding || isMixedUseBuilding;

    const picks = [];

    if (isBusiness) {
        picks.push("business_due_diligence", "tax_structuring", "entity_formation", "investor_visa");
    } else if (isBuilding) {
        // Built-property workflow differs from raw land — no TLTB/EIA, but home inspection is mandatory.
        picks.push("investor_visa", "rbf_inflow");
        picks.push("home_inspection");
        if (isApartment) picks.push("strata_body_corp");
        if (isCommercialBuilding || isMixedUseBuilding) {
            picks.push("commercial_valuation");
            if (zoning && /residential/i.test(zoning) && /commercial/i.test(projectType || "")) {
                picks.push("council_change_of_use");
            }
        }
        if (isFreehold && !isHighValue && isResidentialHome) {
            // residential <FJ$1M is FIRCA-exempt — surface entity_formation instead
            picks.push("entity_formation");
        } else if (isHighValue) {
            picks.push("firca");
        }
    } else {
        // Land / JV — every foreign investor needs these three
        picks.push("investor_visa", "rbf_inflow", "entity_formation");
        if (isItaukeiOrTltb) picks.push("tltb");
        if (isTourism) picks.push("tourism_hotel_aid");
        if (isHighValue) {
            if (!picks.includes("eia")) picks.push("eia");
            if (!picks.includes("council_planning")) picks.push("council_planning");
        }
        if (isFreehold && !isHighValue) picks.push("firca");
        if (kind === "jv" && !picks.includes("investor_match")) picks.push("investor_match");
    }
    // Cap at 4 surfaced services
    return picks.slice(0, 4).map((id) => SERVICE_DEFS[id]).filter(Boolean);
}

function pickTier(context) {
    const { priceFjd, capitalRequiredFjd, projectType, kind } = context;
    const dollars = Number(priceFjd || capitalRequiredFjd || 0) * FJD_TO_USD;
    const isDev = /resort|hotel|tourism|development/i.test(projectType || "") || dollars >= 5_000_000;
    if (isDev) return TIERS.developer;
    if (dollars >= 250_000 || kind === "jv") return TIERS.investor;
    return TIERS.explorer;
}

function pickHeadline(context) {
    const { kind, ownershipType, zoning, projectType, propertyKind } = context;
    const isBusiness = kind === "business";
    const isItaukeiOrTltb = /itaukei|tltb|native|crown/i.test(ownershipType || "");
    const isTourism = /tourism|hotel|resort|hospitality/i.test(`${zoning || ""} ${projectType || ""}`);
    const pk = (propertyKind || "land").toLowerCase();

    if (isBusiness) return "Closing this acquisition needs more than a contract.";
    if (pk === "apartment") return "Apartments need strata + body-corp before settlement.";
    if (pk === "commercial_building") return "Commercial buildings need a yield model + change-of-use consent.";
    if (pk === "mixed_use_building") return "Mixed-use deals: zoning split, separate council applications.";
    if (pk === "residential_home") return "A pre-purchase inspection saves more than it costs.";
    if (isItaukeiOrTltb) return "Fiji's iTaukei land = TLTB. We hold the relationships.";
    if (isTourism) return "Tourism builds qualify for serious tax incentives.";
    if (kind === "jv") return "Joint Ventures need 6+ approvals. We coordinate them all.";
    return "Foreign capital = 7+ government approvals. We handle all of them.";
}

// ---------------------- Main component ----------------------
export default function ServiceConversionSidebar({ context, listingTitle, listingUrl }) {
    const { user } = useAuth();
    const [requestService, setRequestService] = useState(null);

    if (!context) return null;

    const services = pickServices(context);
    const tier = pickTier(context);
    const headline = pickHeadline(context);

    if (services.length === 0) return null;

    return (
        <div data-testid="service-conversion-sidebar" className="space-y-4">
            {/* Contextual services card */}
            <Card className="p-5 bg-card border-coral/30 dark:border-coral/40">
                <div className="flex items-center gap-2 mb-3">
                    <div className="h-8 w-8 rounded-md bg-coral/15 grid place-items-center shrink-0">
                        <Sparkles className="h-4 w-4 text-coral" />
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] font-app font-bold text-coral">
                            Concierge · For this listing
                        </div>
                        <div className="font-display text-base font-bold leading-tight text-foreground">
                            {headline}
                        </div>
                    </div>
                </div>

                <div className="space-y-2">
                    {services.map((s) => {
                        const Icon = s.icon;
                        return (
                            <button
                                key={s.slug}
                                type="button"
                                data-testid={`service-pick-${s.slug}`}
                                onClick={() => setRequestService(s)}
                                className="w-full text-left bg-secondary/40 hover:bg-coral/10 dark:bg-card/60 dark:hover:bg-coral/15 border border-border hover:border-coral/40 rounded-md p-3 transition-all group"
                            >
                                <div className="flex items-start gap-2.5">
                                    <Icon className="h-4 w-4 text-ocean dark:text-turquoise group-hover:text-coral transition mt-0.5 shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-app text-sm font-semibold text-foreground leading-tight mb-0.5">
                                            {s.label}
                                        </div>
                                        <div className="font-app text-xs text-muted-foreground leading-snug">
                                            {s.blurb}
                                        </div>
                                    </div>
                                    <ChevronRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-coral group-hover:translate-x-0.5 transition shrink-0 mt-0.5" />
                                </div>
                            </button>
                        );
                    })}
                </div>

                <Link
                    to="/services"
                    data-testid="service-sidebar-all-services-link"
                    className="block mt-3 text-center font-app text-xs text-muted-foreground hover:text-coral transition"
                >
                    See all 13 services →
                </Link>
            </Card>

            {/* Tier upsell card */}
            <TierUpsellCard tier={tier} context={context} />

            {/* ROI / Government links contextual */}
            {context.kind !== "business" && (
                <Card className="p-4 bg-card">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-app font-bold text-muted-foreground mb-2">
                        Helpful tools
                    </div>
                    <div className="space-y-1.5">
                        <Link
                            to="/government-services"
                            data-testid="service-sidebar-govt-link"
                            className="flex items-center justify-between text-sm font-app text-foreground hover:text-coral transition py-1.5 group"
                        >
                            <span className="flex items-center gap-2">
                                <Network className="h-3.5 w-3.5 text-ocean dark:text-turquoise group-hover:text-coral transition" />
                                Government Liaison Hub
                            </span>
                            <ArrowRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-coral group-hover:translate-x-0.5 transition" />
                        </Link>
                        {(context.kind === "jv" || /tourism|hotel|resort/i.test(`${context.zoning || ""} ${context.projectType || ""}`)) && (
                            <Link
                                to="/tourism"
                                data-testid="service-sidebar-tourism-link"
                                className="flex items-center justify-between text-sm font-app text-foreground hover:text-coral transition py-1.5 group"
                            >
                                <span className="flex items-center gap-2">
                                    <Calculator className="h-3.5 w-3.5 text-fiji-green group-hover:text-coral transition" />
                                    Tourism ROI calculator
                                </span>
                                <ArrowRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-coral group-hover:translate-x-0.5 transition" />
                            </Link>
                        )}
                        <Link
                            to="/invest"
                            data-testid="service-sidebar-quiz-link"
                            className="flex items-center justify-between text-sm font-app text-foreground hover:text-coral transition py-1.5 group"
                        >
                            <span className="flex items-center gap-2">
                                <Sparkles className="h-3.5 w-3.5 text-coral" />
                                60-second investor quiz
                            </span>
                            <ArrowRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-coral group-hover:translate-x-0.5 transition" />
                        </Link>
                    </div>
                </Card>
            )}

            {requestService && (
                <ServiceRequestDialog
                    service={requestService}
                    onClose={() => setRequestService(null)}
                    user={user}
                    listingTitle={listingTitle}
                    listingUrl={listingUrl}
                    context={context}
                />
            )}
        </div>
    );
}

function TierUpsellCard({ tier, context }) {
    const Icon = tier.icon;
    const accentClasses = {
        "fiji-green": "border-fiji-green/30 hover:border-fiji-green/60 bg-fiji-green/5 dark:bg-fiji-green/10",
        coral: "border-coral/40 hover:border-coral/70 bg-coral/5 dark:bg-coral/10",
        ocean: "border-ocean/30 hover:border-ocean/50 dark:border-turquoise/30 dark:hover:border-turquoise/60 bg-ocean/5 dark:bg-turquoise/10",
    };
    const iconColors = {
        "fiji-green": "text-fiji-green",
        coral: "text-coral",
        ocean: "text-ocean dark:text-turquoise",
    };
    return (
        <Card data-testid={`service-sidebar-tier-${tier.id}`} className={`p-5 bg-card border ${accentClasses[tier.accent]} transition-all`}>
            <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline" className="font-app text-[10px] uppercase tracking-wider border-coral/30 text-coral">
                    Recommended for this listing
                </Badge>
            </div>
            <div className="flex items-start gap-3 mb-3">
                <div className={`h-10 w-10 rounded-md grid place-items-center shrink-0 ${
                    tier.accent === "fiji-green" ? "bg-fiji-green/15" :
                    tier.accent === "coral" ? "bg-coral/15" :
                    "bg-ocean/15 dark:bg-turquoise/15"
                }`}>
                    <Icon className={`h-5 w-5 ${iconColors[tier.accent]}`} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app font-bold">
                        Concierge tier
                    </div>
                    <div className="font-display text-xl font-bold leading-tight text-foreground">
                        {tier.label} · <span className="tabular-nums">{tier.price}</span>
                    </div>
                </div>
            </div>
            <p className="font-app text-sm text-foreground/80 leading-relaxed mb-4">
                {tier.oneLiner}
            </p>
            <Button
                asChild
                data-testid={`service-sidebar-tier-cta-${tier.id}`}
                className="w-full bg-coral hover:bg-coral-deep text-white font-app"
            >
                <Link to="/concierge">
                    See concierge tiers <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
                </Link>
            </Button>
        </Card>
    );
}

function ServiceRequestDialog({ service, onClose, user, listingTitle, listingUrl, context }) {
    const [form, setForm] = useState({
        full_name: user?.full_name || "",
        email: user?.email || "",
        phone: user?.phone_e164 || "",
        country: "",
        message: `I'd like ${service.label} handled for the listing I'm viewing.\n\n${
            listingTitle ? `Listing: ${listingTitle}\n` : ""
        }${listingUrl ? `URL: ${listingUrl}\n` : ""}${
            context.priceFjd ? `Asking: FJ$${Number(context.priceFjd).toLocaleString()}\n` : ""
        }${context.capitalRequiredFjd ? `Capital required: FJ$${Number(context.capitalRequiredFjd).toLocaleString()}\n` : ""}\nNotes:\n`,
        budget_band: "",
        timeline: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.full_name || !form.email || !form.message) {
            toast.error("Name, email, and message required");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/services/request", {
                ...form,
                service_slug: service.slug,
            });
            setDone(true);
            toast.success("Request sent — we'll reply within one business day.");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not send request");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="service-sidebar-request-dialog" className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-display text-foreground">
                        Handle: {service.label}
                    </DialogTitle>
                    <DialogDescription className="font-app text-muted-foreground">
                        We'll reply within one business day with scope, timeline, and a quote.
                    </DialogDescription>
                </DialogHeader>

                {done ? (
                    <div className="py-8 text-center" data-testid="service-sidebar-request-done">
                        <ShieldCheck className="h-12 w-12 text-fiji-green mx-auto mb-3" />
                        <div className="font-display text-xl font-bold text-foreground mb-2">Request received</div>
                        <p className="font-app text-sm text-muted-foreground">
                            Reply incoming to <strong className="text-foreground">{form.email}</strong>.
                        </p>
                        <Button onClick={onClose} className="mt-5 bg-ocean-green text-white">Close</Button>
                    </div>
                ) : (
                    <form onSubmit={submit} className="space-y-3" data-testid="service-sidebar-request-form">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs font-app text-muted-foreground">Full name *</Label>
                                <Input data-testid="ssrd-name" value={form.full_name} onChange={(e) => update("full_name", e.target.value)} required className="bg-card text-foreground" />
                            </div>
                            <div>
                                <Label className="text-xs font-app text-muted-foreground">Email *</Label>
                                <Input data-testid="ssrd-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} required className="bg-card text-foreground" />
                            </div>
                            <div>
                                <Label className="text-xs font-app text-muted-foreground">Phone (E.164)</Label>
                                <Input data-testid="ssrd-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+1..." className="bg-card text-foreground" />
                            </div>
                            <div>
                                <Label className="text-xs font-app text-muted-foreground">Country</Label>
                                <Input data-testid="ssrd-country" value={form.country} onChange={(e) => update("country", e.target.value)} placeholder="USA / Australia / NZ…" className="bg-card text-foreground" />
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs font-app text-muted-foreground">Context *</Label>
                            <Textarea data-testid="ssrd-message" value={form.message} onChange={(e) => update("message", e.target.value)} rows={5} required className="bg-card text-foreground resize-none" />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="ghost" onClick={onClose} className="font-app">Cancel</Button>
                            <Button type="submit" disabled={submitting} data-testid="ssrd-submit-btn" className="bg-coral hover:bg-coral-deep text-white">
                                {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                                {submitting ? "Sending…" : "Send request"}
                            </Button>
                        </DialogFooter>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}
